/** How often an open tab asks whether a newer build is deployed. */
export const APP_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export function versionJsonUrl(baseUrl = '/') {
    if (baseUrl === '' || baseUrl === '/')
        return '/version.json';
    return `${baseUrl.replace(/\/$/, '')}/version.json`;
}
export function parseAppVersion(body) {
    try {
        const parsed = JSON.parse(body);
        if (!parsed || typeof parsed !== 'object' || !('buildId' in parsed))
            return null;
        const buildId = parsed.buildId;
        if (typeof buildId !== 'string' || buildId.trim() === '')
            return null;
        return buildId;
    }
    catch {
        return null;
    }
}
export function decideAppUpdate(input) {
    void input.unsaved;
    return {
        showBanner: input.remoteBuildId !== null && input.remoteBuildId !== input.runningBuildId,
    };
}
let unsavedHolds = 0;
let userEdited = false;
/** Hold while Edit (or another in-memory form) is open, even before the first keystroke. */
export function holdUnsavedWork() {
    unsavedHolds += 1;
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        unsavedHolds = Math.max(0, unsavedHolds - 1);
    };
}
/** A field the person has actually changed. Stays set until the tab reloads. */
export function noteUserEdit() {
    userEdited = true;
}
export function hasUnsavedWork() {
    return unsavedHolds > 0 || userEdited;
}
export function resetUnsavedWorkForTests() {
    unsavedHolds = 0;
    userEdited = false;
}
function defaultFetch(input, init) {
    const host = globalThis;
    return host.fetch(input, init);
}
/** `cache: 'no-store'` bypasses the HTTP cache. A network error means "no prompt". */
export async function fetchRemoteBuildId(options) {
    const url = options?.url ?? '/version.json';
    const fetchImpl = options?.fetchImpl ?? defaultFetch;
    try {
        const response = await fetchImpl(url, { cache: 'no-store' });
        if (!response.ok)
            return null;
        return parseAppVersion(await response.text());
    }
    catch {
        return null;
    }
}
export function watchAppUpdate(options) {
    let stopped = false;
    let checking = false;
    const check = () => {
        if (stopped || checking)
            return;
        checking = true;
        void Promise.resolve(options.onServiceWorkerCheck?.())
            .catch(() => undefined)
            .then(() => options.fetchRemoteBuildId())
            .then((remoteBuildId) => {
            checking = false;
            if (stopped)
                return;
            options.onDecision(decideAppUpdate({
                runningBuildId: options.runningBuildId,
                remoteBuildId,
                unsaved: options.hasUnsavedWork(),
            }));
        })
            .catch(() => {
            checking = false;
        });
    };
    check();
    const timers = globalThis;
    const setIntervalFn = options.setIntervalFn ?? timers.setInterval.bind(timers);
    const clearIntervalFn = options.clearIntervalFn ?? timers.clearInterval.bind(timers);
    const timer = setIntervalFn(check, options.intervalMs ?? APP_UPDATE_CHECK_INTERVAL_MS);
    const unsubscribe = (options.subscribeFocus ?? subscribeWindowFocus)(check);
    return () => {
        stopped = true;
        clearIntervalFn(timer);
        unsubscribe();
    };
}
/** How long Reload waits for the new worker to take control before reloading anyway. */
export const APP_UPDATE_RELOAD_FALLBACK_MS = 4000;
function workerInstallSettled(state) {
    return (state === 'installed' ||
        state === 'activating' ||
        state === 'activated' ||
        state === 'redundant');
}
/**
 * `registration.update()` can still be installing when it resolves. Posting
 * SKIP_WAITING before the worker is waiting drops the message, and the
 * following reload keeps the previous worker.
 */
export function waitForWaitingWorker(registration) {
    const ready = () => registration.waiting ??
        (registration.installing?.state === 'installed' ? registration.installing : null);
    const already = ready();
    if (already)
        return Promise.resolve(already);
    const installing = registration.installing;
    if (!installing || workerInstallSettled(installing.state)) {
        return Promise.resolve(registration.waiting);
    }
    return new Promise((resolve) => {
        const finish = () => {
            installing.removeEventListener('statechange', finish);
            resolve(registration.waiting ?? (installing.state === 'installed' ? installing : null));
        };
        installing.addEventListener('statechange', () => {
            if (workerInstallSettled(installing.state))
                finish();
        });
        if (workerInstallSettled(installing.state))
            finish();
    });
}
export async function activateWaitingServiceWorker(registration) {
    await registration.update();
    const waiting = await waitForWaitingWorker(registration);
    if (!waiting || waiting.state !== 'installed')
        return 'current';
    waiting.postMessage({ type: 'SKIP_WAITING' });
    return 'skipped';
}
/**
 * Ask a waiting field worker to take over, then reload once. With no worker
 * (the admin app, or a worker that is already current) this reloads immediately.
 * The controllerchange listener exists only for this call, so a first install's
 * clientsClaim does not reload the page by itself.
 */
export async function reloadForAppUpdate(host) {
    let finished = false;
    const finish = () => {
        if (finished)
            return;
        finished = true;
        host.clearTimeout(timer);
        host.reload();
    };
    const timer = host.setTimeout(finish, APP_UPDATE_RELOAD_FALLBACK_MS);
    const sw = host.serviceWorker;
    if (!sw) {
        finish();
        return;
    }
    sw.addEventListener('controllerchange', finish, { once: true });
    try {
        const registration = await sw.getRegistration();
        if (!registration) {
            finish();
            return;
        }
        const outcome = await activateWaitingServiceWorker(registration);
        if (outcome === 'current')
            finish();
    }
    catch {
        finish();
    }
}
function subscribeWindowFocus(listener) {
    const host = globalThis;
    const win = host.window;
    const doc = host.document;
    if (!win || !doc)
        return () => { };
    const onFocus = () => listener();
    const onVisible = () => {
        if (doc.visibilityState === 'visible')
            listener();
    };
    win.addEventListener('focus', onFocus);
    doc.addEventListener('visibilitychange', onVisible);
    return () => {
        win.removeEventListener('focus', onFocus);
        doc.removeEventListener('visibilitychange', onVisible);
    };
}
//# sourceMappingURL=app-update.js.map